import React, {
  useEffect,
  useState,
  ReactElement,
  Dispatch,
  SetStateAction,
  HTMLAttributes,
} from 'react';
import usePagination from './usePagination';
import { Button, Select } from '../index';

export type PaginationDetails = {
  currentPage: number;
  totalPages: number;
  listLength: number;
  itemsPerPage: number;
};

interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  list: any[];
  children: ReactElement;
  label?: string;
  description?: string;
  itemsPerPage?: number;
  showOptions?: boolean;
  positionControls?: 'center' | 'right' | 'left';
  topMargin?: number;
  onClick?: () => void;
  setPaginationDetails?: Dispatch<SetStateAction<PaginationDetails>>;
}
function Pagination({
  list = [],
  showOptions = true,
  itemsPerPage = 10,
  children,
  onClick,
  positionControls,
  topMargin,
  setPaginationDetails,
}: PaginationProps) {
  const {
    pages,
    page,
    setPage,
    nextPage,
    prevPage,
    setPerPage,
    setTotal,
    startIndex,
    endIndex,
  } = usePagination(itemsPerPage, list.length);

  useEffect(() => {
    setPaginationDetails &&
      setPaginationDetails({
        listLength: list.length,
        itemsPerPage,
        currentPage: page,
        totalPages: pages,
      });
  }, [list, itemsPerPage, page, pages]);

  useEffect(() => {
    setTotal(list.length);
    if (list.slice(startIndex, endIndex).length === 0) {
      if (page > 1) {
        setPage((currPage) => currPage - 1);
      } else {
        setPage(1);
      }
    }
  }, [list]);

  return (
    <div>
      <div className="table-container">
        {list.slice(startIndex, endIndex).map((item, index) => {
          const newProps =
            typeof item === 'object' ? { ...item } : { text: item };
          return (
            // many users do not put in the id for an item, so the template string is the usual expected behavior
            <React.Fragment key={item.id || `page-${page}-index-${index}`}>
              {React.cloneElement(children, newProps)}
            </React.Fragment>
          );
        })}
      </div>
      {list.length > itemsPerPage && (
        <PaginationControls
          nextPage={nextPage}
          prevPage={prevPage}
          setPage={setPage}
          page={page}
          pages={pages}
          setPerPage={setPerPage}
          showOptions={showOptions}
          onClick={onClick}
          positionControls={positionControls}
          topMargin={topMargin}
        />
      )}
    </div>
  );
}

export default Pagination;

const paginationOptions = [
  { name: '10', value: '10' },
  { name: '25', value: '25' },
  { name: '50', value: '50' },
];

interface ControlsProps {
  nextPage: () => void;
  prevPage: () => void;
  setPage: (pageNumber: number) => void;
  page: number;
  pages: number;
  setPerPage: (quantity: number) => void;
  label?: string;
  description?: string;
  showOptions?: boolean;
  onClick?: () => void;
  positionControls?: 'center' | 'right' | 'left';
  topMargin?: number;
}

const PaginationControls = ({
  nextPage,
  prevPage,
  setPage,
  page,
  pages,
  setPerPage,
  showOptions = true,
  onClick,
  positionControls,
  topMargin,
}: ControlsProps) => {
  const [pageButtons, setPageButtons] = useState<number[]>([]);

  const handleOnClickCallBack = () => {
    onClick && onClick();
  };

  const handleSetFirstPage = () => {
    handleOnClickCallBack();
    setPage(1);
  };

  const handleSetPreviousPage = () => {
    handleOnClickCallBack();
    prevPage();
  };

  const handleSetNextPage = () => {
    handleOnClickCallBack();
    nextPage();
  };

  const handleSetPage = (value: number) => {
    handleOnClickCallBack();
    setPage(value);
  };

  const handleControlPosition = () => {
    if (positionControls === 'left') return 'justify-start';
    if (positionControls === 'right') return 'justify-end';
    return 'justify-center';
  };

  // Handle which buttons to display
  useEffect(() => {
    // Set the first and last of 5 buttons within bounds of the available pages.
    let firstButton = page - 2;
    const lastButton = firstButton + 4;
    firstButton = lastButton > pages ? pages - 4 : firstButton;
    firstButton = firstButton <= 0 ? 1 : firstButton;

    const length = pages < 5 ? pages : 5;
    const buttonArray = Array.from({ length }, (_, i) => i + firstButton);
    setPageButtons(buttonArray);
  }, [page, pages]);

  return (
    <div
      className={`flex flex-row gap-x-10 w-full mt-4 scale-95 ${handleControlPosition()}`}
      style={topMargin ? { marginTop: `${topMargin}px` } : undefined}
    >
      <div className="flex flex-nowrap gap-x-2">
        <Button
          className={`text-lg ${disabledBtnStyles} ${btnStyles}`}
          variant="secondary"
          buttonType="outlined"
          onClick={handleSetFirstPage}
          disabled={page === 1}
          aria-label="Set to page 1"
          aria-describedby="Click to open page 1"
        >
          &#171;
        </Button>
        <Button
          className={`text-lg ${disabledBtnStyles} ${btnStyles}`}
          variant="secondary"
          buttonType="outlined"
          onClick={handleSetPreviousPage}
          disabled={page === 1}
          aria-label="Previous page"
          aria-describedby={`Click to open page ${page - 1}`}
        >
          &#8249;
        </Button>
        {pageButtons.map((button) => (
          <Button
            key={button}
            className={`sm:hidden min-w-min ${
              button === page ? selectedBtnStyles : btnStyles
            }`}
            variant={button === page ? 'primary' : 'secondary'}
            buttonType={button !== page ? 'outlined' : undefined}
            onClick={() => handleSetPage(button)}
            aria-label={`Page ${button}`}
            aria-describedby={`Click to open page ${button}`}
          >
            {button}
          </Button>
        ))}
        {/* {pageButtons[pageButtons.length - 1] < pages && <Button variant="secondary" buttonType="outlined">...</Button>} */}
        {}
        <Button
          className={`hidden sm:block`}
          variant={'primary'}
          buttonType={'outlined'}
        >
          {page} / {pages}
        </Button>
        <Button
          className={`text-lg ${disabledBtnStyles} ${btnStyles}`}
          variant="secondary"
          buttonType="outlined"
          onClick={handleSetNextPage}
          disabled={page === pages}
          aria-label={`Next Page`}
          aria-describedby={`Click to next page ${page + 1}`}
        >
          &#8250;
        </Button>
        <Button
          className={`text-lg ${disabledBtnStyles} ${btnStyles}`}
          variant="secondary"
          buttonType="outlined"
          onClick={() => handleSetPage(pages)}
          disabled={page === pages}
          aria-label={`Last Page`}
          aria-describedby={`Click to last page ${pages}`}
        >
          &#187;
        </Button>
      </div>
      {showOptions && (
        <Select
          options={paginationOptions}
          selectLabel="Select"
          selectedOption={paginationOptions[0]}
          setSelectedOption={(e: any) => setPerPage(parseInt(e.value))}
          className={`sm:hidden`}
        />
      )}
    </div>
  );
};

const btnStyles = 'border-thryv-gray-medium-500 hover:bg-thryv-gray-medium-500';
const selectedBtnStyles =
  'border-thryv-gray-medium-500 hover:bg-thryv-orange-700';
const disabledBtnStyles =
  'disabled:border-thryv-gray-medium-100 disabled:text-thryv-gray-medium-200';
